import type { SponsorCampaign } from "../campaignTypes";

/**
 * ComedyUO Food Tour ABM microsite config.
 * Swap brand fields / image URLs here without touching FoodTourPage layout.
 */

export const HERO_IMAGE_URL =
  "https://storage.googleapis.com/cuo-website/Chris%20Redd%20FT.png";
export const NEIGHBORHOOD_CLASSICS_IMAGE_URL =
  "https://storage.googleapis.com/cuo-website/Dan+Soder+at+Veselka.jpg";
export const TIMES_SQUARE_IMAGE_URL =
  "https://storage.googleapis.com/cuo-website/Peluffo%20Margarita.png";
export const BRAND_LOGO_URL = "";

export const VENUE_IMAGE_URLS = {
  zabars: "https://storage.googleapis.com/cuo-website/Dave+Attell+at+Zabar's.jpg",
  barneyGreengrass: "https://storage.googleapis.com/cuo-website/Mark%20Barney%20Green%20(Small).jpg",
  veselka: "https://storage.googleapis.com/cuo-website/Dan+Soder+at+Veselka.jpg",
  veselkaAlt: "https://storage.googleapis.com/cuo-website/Food%20at%20Veselka%202%20(small).jpg",
  veselkaRoom: "https://storage.googleapis.com/cuo-website/Dan+Soder+at+Veselka.jpg",
  russDaughters: "https://storage.googleapis.com/cuo-website/Jared%20FT.png",
  economyCandy: "https://storage.googleapis.com/cuo-website/Chris+Redd+at+Economy+Candy.jpg",
  apolloBagels: "https://storage.googleapis.com/cuo-website/Apollo%20Bagels.png",
  applebees: "/images/partners/food-tour/applebees-times-square.jpg",
  margaritaville: "https://storage.googleapis.com/cuo-website/Peluffo%20Margarita.png",
} as const;

export const CONTENT_IMAGE_URLS = {
  ownerInterview:
    "https://storage.googleapis.com/cuo-website/Barney%20Greengrass%20Interview%20(small).jpg",
  comedianOnSite:
    "https://storage.googleapis.com/cuo-website/Chris%20Redd%20Food%20Tour%20(small).jpg",
  kitchenMoment: "https://storage.googleapis.com/cuo-website/Food%20at%20Barney%20(small).jpg",
  venueStory: "https://storage.googleapis.com/cuo-website/Dan+Soder+at+Veselka.jpg",
  socialAsset: "https://storage.googleapis.com/cuo-website/Jared%20(Small).jpg",
} as const;

export type FoodTourVenue = {
  id: string;
  name: string;
  cuisine: string;
  week: "neighborhood" | "timesSquare";
  dateLabel: string;
  capacity: number;
  imageUrl: string;
  note: string;
};

export type FoodTourSponsorshipTier = {
  weekOneDates: string[];
  weekTwoDates: string[];
  fullTour: {
    supporterPrice: string;
    sponsorPrice: string;
    supporterGets: string[];
    sponsorGets: string[];
    youProvide: string[];
  };
  weekOneOnly: {
    label: string;
    supporterPrice: string;
    sponsorPrice: string;
  };
};

export type FoodTourConfig = {
  brandName: string;
  brandLogoUrl: string;
  accentColor: string;
  partnershipThesis: string;
  brandWhyBody: string[];
  brandActivations: {
    onSite: string[];
    content: string[];
    guestAccess: string[];
    onSiteEyebrow?: string;
    contentEyebrow?: string;
    guestEyebrow?: string;
  };
  howBrandTitle?: string;
  weekCopy: {
    neighborhood: { title: string; body: string };
    timesSquare: { title: string; body: string };
  };
  tourSectionTitle: string;
  tourSectionIntro: string;
  nightFlow: {
    title: string;
    intro: string;
    steps: { time: string; title: string; body: string }[];
  };
  heroFacts: { label: string; value: string }[];
  sponsorshipTiers?: FoodTourSponsorshipTiers;
  tourTitle: string;
  timingLabel: string;
  positioning: string;
  venues: FoodTourVenue[];
  stats: { label: string; value: string; detail: string }[];
  cta: {
    headline: string;
    body: string;
    email: string;
    name: string;
    role: string;
  };
  images: {
    hero: string;
    neighborhood: string;
    timesSquare: string;
    content: typeof CONTENT_IMAGE_URLS;
  };
};

/** Central editable object for the Food Tour ABM page. */
export const foodTourConfig: FoodTourConfig = {
  // Leave blank for the shared tour overview. Set a name/logo to personalize for one brand.
  brandName: "",
  brandLogoUrl: BRAND_LOGO_URL,
  accentColor: "#D4A574",
  partnershipThesis:
    "The right partner belongs beside the owners who keep New York running",
  brandWhyBody: [
    "This tour is built around the people behind eight New York food businesses. Owners, operators, and the teams that make each room work.",
    "A brand that already stands with small businesses fits a night that puts those same businesses on stage, with comedy, food, and culture in the same frame.",
    "Eight sold-out rooms, eight owner stories, and a two-week cultural arc create a human New York setting that no banner buy can invent.",
  ],
  brandActivations: {
    onSite: [
      "Experiential takeover of entryways and on-site touchpoints at every venue",
      "Countertop signage and point-of-connection materials across the tour",
      "Dedicated shout-out at every show",
      "Opportunity to distribute giveaways and take-home moments for guests",
      "Reserved seating for your team across the tour",
    ],
    content: [
      "Logo featured across promotional materials, flyer, and paid/organic assets",
      "Feature in the tour promo video and the full recap film",
      "Dedicated grid posts on Instagram and TikTok, plus five Instagram story posts",
      "Feature on the email blast to the 38,000-person waitlist",
      "Organic inclusion on influencer posts supporting the tour",
    ],
    guestAccess: [
      "10 tickets to the tour with reserved seating for your team",
      "Products and materials for on-site and social giveaways",
      "Category exclusivity so no competing brand shares the tour",
      "No sponsor fee for a future ComedyUO show",
      "Collaboration on tagged Instagram stories from venue and ComedyUO accounts",
    ],
  },
  tourTitle: "The 2027 Big/Small Business Food Tour",
  timingLabel: "January 2027",
  positioning:
    "Eight iconic New York restaurants. Intimate sold-out comedy. A partner that belongs with the owners behind the rooms.",
  howBrandTitle: "Built into the night, not stuck on top of it.",
  weekCopy: {
    neighborhood: {
      title: "Neighborhood Classics",
      body: "Multigenerational institutions like Zabar's, Barney Greengrass, Veselka, Russ & Daughters, and Economy Candy. The beating heart of NYC, after hours.",
    },
    timesSquare: {
      title: "Times Square Icons",
      body: "Apollo Bagels, Margaritaville, and Applebee's. Locals, visitors, and household names in rooms no traditional comedy club could invent.",
    },
  },
  tourSectionTitle: "Eight nights across New York.",
  tourSectionIntro:
    "Monday through Friday across two weeks. Each night has its own comics, its own room, and its own owner story. Catch the whole tour and you will likely see more than 30 different comedians.",
  nightFlow: {
    title: "A typical night.",
    intro: "Same spine every stop. Different room, different comics, same sold-out energy.",
    steps: [
      {
        time: "6:30",
        title: "Doors",
        body: "Guests roll in. Drinks and house bites from the restaurant. The room fills fast.",
      },
      {
        time: "7:15",
        title: "Settle in",
        body: "Owner energy in the room. People grab seats. Partner presence lands before the first joke.",
      },
      {
        time: "7:30",
        title: "The show",
        body: "Host plus a surprise lineup. Intimate, sold out, and not a club night.",
      },
      {
        time: "9:00",
        title: "The after",
        body: "Guests linger. Photos, leftovers, sidewalk spillover. The night keeps talking.",
      },
    ],
  },
  heroFacts: [
    { label: "Venues", value: "8" },
    { label: "Guests", value: "1,000" },
    { label: "Comedians", value: "35+" },
    { label: "Weeks", value: "2" },
  ],
  venues: [
    {
      id: "veselka",
      name: "Veselka",
      cuisine: "Ukrainian cuisine",
      week: "neighborhood",
      dateLabel: "Jan 11",
      capacity: 90,
      imageUrl: VENUE_IMAGE_URLS.veselka,
      note: "A multigenerational East Village institution after hours.",
    },
    {
      id: "barney-greengrass",
      name: "Barney Greengrass",
      cuisine: "American Jewish deli",
      week: "neighborhood",
      dateLabel: "Jan 12",
      capacity: 40,
      imageUrl: VENUE_IMAGE_URLS.barneyGreengrass,
      note: "An intimate Upper West Side classic with room for only a few dozen.",
    },
    {
      id: "russ-daughters",
      name: "Russ & Daughters",
      cuisine: "Bagel specialties",
      week: "neighborhood",
      dateLabel: "Jan 13",
      capacity: 180,
      imageUrl: VENUE_IMAGE_URLS.russDaughters,
      note: "Appetizing counter energy turned into a comedy room.",
    },
    {
      id: "zabars",
      name: "Zabar's",
      cuisine: "American Jewish grocer",
      week: "neighborhood",
      dateLabel: "Jan 14",
      capacity: 110,
      imageUrl: VENUE_IMAGE_URLS.zabars,
      note: "A neighborhood landmark New York already treats like theater.",
    },
    {
      id: "economy-candy",
      name: "Economy Candy",
      cuisine: "Candy and dessert",
      week: "neighborhood",
      dateLabel: "Jan 15",
      capacity: 110,
      imageUrl: VENUE_IMAGE_URLS.economyCandy,
      note: "Sweet, packed, and pure Lower East Side.",
    },
    {
      id: "applebees",
      name: "Applebee's",
      cuisine: "Times Square favorite",
      week: "timesSquare",
      dateLabel: "Jan 20",
      capacity: 120,
      imageUrl: VENUE_IMAGE_URLS.applebees,
      note: "I mean, c'mon now.",
    },
    {
      id: "margaritaville",
      name: "Margaritaville",
      cuisine: "Times Square favorite",
      week: "timesSquare",
      dateLabel: "Jan 21",
      capacity: 200,
      imageUrl: VENUE_IMAGE_URLS.margaritaville,
      note: "The one and only.",
    },
    {
      id: "apollo-bagels",
      name: "Apollo Bagels",
      cuisine: "Needs no introduction",
      week: "timesSquare",
      dateLabel: "Jan 22",
      capacity: 110,
      imageUrl: VENUE_IMAGE_URLS.apolloBagels,
      note: "Familiar name, unexpected night.",
    },
  ],
  stats: [
    {
      label: "Tour scale",
      value: "8 venues",
      detail: "1,000 guests across two weeks in January 2027",
    },
    {
      label: "Talent",
      value: "35+ comedians",
      detail: "A distinct lineup each night so the tour never repeats itself",
    },
    {
      label: "ComedyUO demand",
      value: "38,000",
      detail: "People on the waitlist for first access to tickets",
    },
    {
      label: "Venue reach",
      value: "1.375M",
      detail: "Combined followers across the eight partner businesses",
    },
    {
      label: "Track record",
      value: "350+ shows",
      detail: "More than 250 comics and well over $200,000 earned for small businesses",
    },
  ],
  cta: {
    headline: "Let's build the activation together.",
    body: "The rooms, the owners, the talent, and the content calendar are ready. Tell us how you want to show up and we'll map the nights.",
    email: "mike@comedyuo.com",
    name: "Mike Zbyszewski",
    role: "Sales & Partnerships, ComedyUO",
  },
  images: {
    hero: HERO_IMAGE_URL,
    neighborhood: NEIGHBORHOOD_CLASSICS_IMAGE_URL,
    timesSquare: TIMES_SQUARE_IMAGE_URL,
    content: CONTENT_IMAGE_URLS,
  },
};

/**
 * Helmet / gate fields for the partner route. Layout reads foodTourConfig.
 */
export const foodTourCampaign: SponsorCampaign = {
  campaignSlug: "food-tour",
  sponsorName: foodTourConfig.brandName || "ComedyUO Food Tour",
  sponsorLogo: foodTourConfig.brandLogoUrl || "/images/partners/food-tour/brand-fallback.svg",
  sponsorWordmark: foodTourConfig.brandName || "Food Tour",
  comedyuoLogo: "https://storage.googleapis.com/cuo-website/comedyuo-logo-white.png",
  accentColor: foodTourConfig.accentColor,
  eyebrow: foodTourConfig.brandName
    ? `Prepared for ${foodTourConfig.brandName} · Partnership concept`
    : "ComedyUO Food Tour · Partnership concept",
  campaignName: foodTourConfig.tourTitle,
  shareTitle: foodTourConfig.brandName
    ? `${foodTourConfig.brandName} / ComedyUO Food Tour`
    : "ComedyUO Food Tour",
  shareImage: foodTourConfig.images.hero || "/images/og-default.jpg",
  headline: foodTourConfig.tourTitle,
  venue: "Eight iconic New York restaurants",
  dateRange: foodTourConfig.timingLabel,
  eventDate: "January 11–15 & 20–22, 2027",
  reportingPeriod: "Partnership concept",
  campaignSummary: foodTourConfig.positioning,
  heroImage: foodTourConfig.images.hero,
  heroImageAlt: "ComedyUO Food Tour night with Chris Redd",
  heroCtaLabel: "See the tour",
  heroCtaHref: "#big-idea",
  featuredMetric: {
    label: "Tour",
    value: "8 nights",
    detail: "1,000 guests · January 2027",
  },
  glanceFields: [],
  activations: [],
  activationsSection: { eyebrow: "", title: "", description: "" },
  platformPerformance: {
    eyebrow: "",
    title: "",
    description: "",
    rows: [],
    totalImpressionsLabel: "",
    totalImpressionsValue: "",
  },
  kpiSection: { eyebrow: "", title: "", description: "", groups: [] },
  gallery: { eyebrow: "", title: "", images: [] },
  assets: { eyebrow: "", title: "", description: "", reportPdf: false, items: [] },
  closing: {
    thankYouHeadline: foodTourConfig.cta.headline,
    summary: foodTourConfig.cta.body,
    highlightCallout: foodTourConfig.partnershipThesis,
    contact: {
      name: foodTourConfig.cta.name,
      role: foodTourConfig.cta.role,
      email: foodTourConfig.cta.email,
    },
  },
  allowedEmailDomains: ["comedyuo.com"],
  nav: [
    { id: "big-idea", label: "The big idea", href: "#big-idea" },
    { id: "two-sides", label: "Two sides", href: "#two-sides" },
    { id: "the-tour", label: "The tour", href: "#the-tour" },
    {
      id: "why-brand",
      label: foodTourConfig.brandName ? `Why ${foodTourConfig.brandName}` : "Why partner",
      href: "#why-brand",
    },
    { id: "how-brand", label: "How you show up", href: "#how-brand" },
    { id: "content", label: "Content", href: "#content" },
    { id: "reach", label: "Reach", href: "#reach" },
    { id: "vision", label: "Next", href: "#vision" },
  ],
  isSamplePlaceholder: false,
  sampleDisclaimer: "",
};
